// Trạng thái tu luyện (CULTIVATION §1.5, §5.4, §6.4, §17) — phần của giai đoạn P0.
// Mọi trường có mặc định để migration thêm trường mới không cần dữ liệu cũ.
import { BALANCE } from '@data/balance';
import { REALM_COUNT } from '@data/powerScale';
import { DEFAULT_SITE_ID } from '@data/content/sites';
import { START_TECHNIQUE_ID, type TriOrigin } from '@data/content/techniques';

/** Bế Quan | Lịch Luyện (một ô idle, BALANCE §2.3 `TRIAL_QI_SHARE`). */
export type IdleSlotMode = 'seclusion' | 'trial';
/** Tiểu Bế Quan (`seclusionLight`) | Tử Quan (`deathSeclusion`). */
export type SeclusionKind = 'light' | 'death';

export interface IdleSlotState {
  mode: IdleSlotMode;
  /** Vùng đang Lịch Luyện (khi mode = 'trial'). */
  trialZoneId: string | null;
  /** Nơi bế quan (id CultivationSite). */
  locationId: string;
  seclusion: {
    kind: SeclusionKind;
    startedAtMs: number;
    /** Chỉ Tử Quan. */
    endsAtMs: number | null;
    /** Năm thọ đã trả trước khi vào Tử Quan. */
    lifespanPrepaid: number;
  };
  lastSettledMs: number;
}

export type LifespanSource = 'deathSeclusion' | 'forbiddenArt' | 'demonicBreakthrough' | 'story' | 'pill';

export interface LifespanState {
  /** Tuổi (năm game, float). Chỉ tăng vì lựa chọn chủ động, không bao giờ theo giờ thật. */
  age: number;
  lifespanBonus: number;
  pillsTakenByRealm: number[];
  history: { atMs: number; delta: number; source: LifespanSource }[];
}

export interface TechniqueState {
  mainId: string;
  /** 1..9 */
  layer: number;
  /** Tâm Đắc hiện có. */
  mastery: number;
  known: string[];
  /** Tam Nguyên đã tích (float, không mất lẻ). */
  triOrigin: TriOrigin;
}

export interface Buff {
  id: string;
  bucket: number;
  /** Timestamp tuyệt đối. */
  untilMs: number;
}

export interface CultivationState {
  /** Bước tiểu cảnh toàn cục n = L − 1 (0..40). */
  step: number;
  /** Tu vi trong thanh hiện tại. */
  cultivationXp: number;
  /** Dư Khí. */
  overflowQi: number;
  idle: IdleSlotState;
  lifespan: LifespanState;
  technique: TechniqueState;
  /** Cấp Tụ Linh Trận của động phủ (P1; P0 luôn 0). */
  gatherArrayLv: number;
  buffs: Buff[];
  /** Đan độc 0..100 (CULTIVATION §13). */
  pillToxicity: number;
  /** Phần trang bị cộng vào tu luyện: Hấp Linh %, Bế Quan Hiệu Suất (điểm %). Lớp state cập nhật khi đổi đồ. */
  gear: { qiRatePct: number; offlineEffPct: number };
}

export function defaultCultivation(nowMs: number): CultivationState {
  return {
    step: 0,
    cultivationXp: 0,
    overflowQi: 0,
    idle: {
      mode: 'seclusion',
      trialZoneId: null,
      locationId: DEFAULT_SITE_ID,
      seclusion: { kind: 'light', startedAtMs: nowMs, endsAtMs: null, lifespanPrepaid: 0 },
      lastSettledMs: nowMs,
    },
    lifespan: {
      age: BALANCE.START_AGE,
      lifespanBonus: 0,
      pillsTakenByRealm: Array.from({ length: REALM_COUNT }, () => 0),
      history: [],
    },
    technique: {
      mainId: START_TECHNIQUE_ID,
      layer: 1,
      mastery: 0,
      known: [START_TECHNIQUE_ID],
      triOrigin: { body: 0, spirit: 0, mind: 0 },
    },
    gatherArrayLv: 0,
    buffs: [],
    pillToxicity: 0,
    gear: { qiRatePct: 0, offlineEffPct: 0 },
  };
}
